import { type FC, memo, type ReactNode } from "react";

import classNames from "classnames";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

// const DAYS_IN_A_WEEK = 7;
const days = [1, 2, 3, 4, 5, 6, 7];

interface Props {
  className?: string;
  visibleMonth: Date;
  selectedDate: Date | null;

  onClick?: (date: Date) => void;
  isDayDisabled?: (date: Date) => boolean;
  cssClasses?: (date: Date) => string;
}

const Calendar: FC<Props> = (props) => {
  const { className, visibleMonth, onClick, isDayDisabled, cssClasses, selectedDate = new Date() } = props;

  const { t } = useTranslation();

  const firstDayOfMonth = dayjs(visibleMonth).startOf("month");
  const firstDay = dayjs(firstDayOfMonth).startOf("week");
  const lastDayOfMonth = dayjs(visibleMonth).endOf("month");
  const lastDay = dayjs(lastDayOfMonth).endOf("week");

  const weeks = [];
  let dayCells: ReactNode[];

  let day = dayjs(firstDay);
  let dayIndex = 0;

  while (day.isBefore(lastDay)) {
    if (dayIndex === 0) {
      dayCells = [];
      weeks.push(dayCells);
    }

    dayIndex = (dayIndex + 1) % 7;

    const thisDay = dayjs(day).toDate();
    const isDisabled = isDayDisabled ? isDayDisabled(thisDay) : false;

    const cls = classNames(
      {
        "scha__calendar__day-item": true,
        "scha__calendar__day-item--current-month": !day.isBefore(firstDayOfMonth) && !day.isAfter(lastDayOfMonth),
        "scha__calendar__day-item--selected": selectedDate ? day.isSame(selectedDate, "day") : false,
        disabled: isDisabled,
      },
      cssClasses && cssClasses(thisDay)
    );

    const onClickHandler = !isDisabled && onClick ? () => onClick(thisDay) : undefined;

    dayCells!.push(
      <div
        key={day.get("date")}
        className={cls}
        onClick={onClickHandler}
        data-test-id={`calendar-date-${day.get("month")}-${day.get("day")}`}
      >
        {day.get("date")}
      </div>
    );

    day = day.add(1, "day");
  }

  return (
    <div className={classNames("scha__calendar", className)}>
      <div className="scha__calendar__header">
        {days.map((label, index) => (
          <div key={index} className="scha__calendar__header-item">
            {t("dayShort." + label)}
          </div>
        ))}
      </div>
      <div className="scha__calendar__days">
        {weeks.map((dayList, index) => (
          <div key={index} className="scha__calendar__week-item">
            {dayList}
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(Calendar);
